/**
 * CandlestickChart — lightweight-charts React wrapper.
 * Exposes priceToCoordinate and crosshair/range change callbacks
 * so the parent can sync a vertical bin panel to the same Y scale.
 */

import {
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useCallback,
} from 'react';
import {
  createChart,
  CandlestickSeries,
  type IChartApi,
  type ISeriesApi,
  type CandlestickData,
  type Time,
  type LogicalRangeChangeEventHandler,
} from 'lightweight-charts';
import { useTheme } from '../../context/ThemeContext';

export interface CandleDataPoint {
  time: number; // unix seconds
  open: number;
  high: number;
  low: number;
  close: number;
}

export interface CandlestickChartHandle {
  /** Convert a price value to a Y pixel coordinate on the chart */
  priceToCoordinate: (price: number) => number | null;
  /** Get the chart's visible price range [low, high] */
  getVisiblePriceRange: () => { low: number; high: number } | null;
}

interface CandlestickChartProps {
  data: CandleDataPoint[];
  width: number;
  height: number;
  /** Called whenever the visible range changes (zoom/pan) */
  onVisibleRangeChange?: () => void;
}

const CandlestickChart = forwardRef<CandlestickChartHandle, CandlestickChartProps>(
  ({ data, width, height, onVisibleRangeChange }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const chartRef = useRef<IChartApi | null>(null);
    const seriesRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    // Stable callback ref for range changes
    const rangeChangeRef = useRef(onVisibleRangeChange);
    rangeChangeRef.current = onVisibleRangeChange;

    const handleRangeChange: LogicalRangeChangeEventHandler = useCallback(() => {
      rangeChangeRef.current?.();
    }, []);

    // Create chart once
    useEffect(() => {
      if (!containerRef.current) return;

      const chart = createChart(containerRef.current, {
        width,
        height,
        layout: {
          background: { color: 'transparent' },
          textColor: isDark ? '#c0c0c0' : '#404040',
          fontSize: 10,
        },
        grid: {
          vertLines: { color: isDark ? '#2a2a2a' : '#e0e0e0' },
          horzLines: { color: isDark ? '#2a2a2a' : '#e0e0e0' },
        },
        rightPriceScale: {
          borderColor: isDark ? '#404040' : '#c0c0c0',
          scaleMargins: { top: 0.1, bottom: 0.1 },
        },
        timeScale: {
          borderColor: isDark ? '#404040' : '#c0c0c0',
          timeVisible: true,
          secondsVisible: false,
        },
        crosshair: {
          vertLine: { color: isDark ? '#555' : '#999', width: 1, style: 3 },
          horzLine: { color: isDark ? '#555' : '#999', width: 1, style: 3 },
        },
      });

      const series = chart.addSeries(CandlestickSeries, {
        upColor: '#26a69a',
        downColor: '#ef5350',
        borderUpColor: '#26a69a',
        borderDownColor: '#ef5350',
        wickUpColor: '#26a69a',
        wickDownColor: '#ef5350',
        priceFormat: { type: 'price', precision: 4, minMove: 0.0001 },
      });

      chartRef.current = chart;
      seriesRef.current = series;

      // Subscribe to range changes for bin sync
      chart.timeScale().subscribeVisibleLogicalRangeChange(handleRangeChange);

      return () => {
        chart.timeScale().unsubscribeVisibleLogicalRangeChange(handleRangeChange);
        chart.remove();
        chartRef.current = null;
        seriesRef.current = null;
      };
      // Only re-create on mount/unmount — theme/size handled by applyOptions
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update theme
    useEffect(() => {
      chartRef.current?.applyOptions({
        layout: {
          background: { color: 'transparent' },
          textColor: isDark ? '#c0c0c0' : '#404040',
        },
        grid: {
          vertLines: { color: isDark ? '#2a2a2a' : '#e0e0e0' },
          horzLines: { color: isDark ? '#2a2a2a' : '#e0e0e0' },
        },
        rightPriceScale: { borderColor: isDark ? '#404040' : '#c0c0c0' },
        timeScale: { borderColor: isDark ? '#404040' : '#c0c0c0' },
      });
    }, [isDark]);

    // Update size
    useEffect(() => {
      chartRef.current?.resize(width, height);
    }, [width, height]);

    // Update data — only fitContent on first load to avoid retriggering range changes
    const dataLoadedRef = useRef(false);
    useEffect(() => {
      if (!seriesRef.current || data.length === 0) return;

      // lightweight-charts requires strictly ascending unique timestamps.
      // The API can return duplicates — deduplicate by keeping the last entry per timestamp.
      const deduped = new Map<number, CandleDataPoint>();
      for (const d of data) {
        deduped.set(d.time, d);
      }
      const formatted: CandlestickData<Time>[] = Array.from(deduped.values())
        .sort((a, b) => a.time - b.time)
        .map((d) => ({
          time: d.time as Time,
          open: d.open,
          high: d.high,
          low: d.low,
          close: d.close,
        }));

      seriesRef.current.setData(formatted);
      if (!dataLoadedRef.current) {
        dataLoadedRef.current = true;
        chartRef.current?.timeScale().fitContent();
      }
    }, [data]);

    // Expose imperative API
    useImperativeHandle(ref, () => ({
      priceToCoordinate: (price: number) => {
        return seriesRef.current?.priceToCoordinate(price) ?? null;
      },
      getVisiblePriceRange: () => {
        const priceScale = chartRef.current?.priceScale('right');
        if (!priceScale) return null;
        // Use the series visible range from autoscale
        const series = seriesRef.current;
        if (!series) return null;
        // Get the coordinate of the chart area
        const chartHeight = height;
        // Walk top and bottom pixel coordinates to find price range
        const topPrice = series.coordinateToPrice(0);
        const bottomPrice = series.coordinateToPrice(chartHeight);
        if (topPrice == null || bottomPrice == null) return null;
        return {
          low: Math.min(topPrice as number, bottomPrice as number),
          high: Math.max(topPrice as number, bottomPrice as number),
        };
      },
    }), [height]);

    return (
      <div
        ref={containerRef}
        style={{ width, height }}
      />
    );
  },
);

CandlestickChart.displayName = 'CandlestickChart';
export default CandlestickChart;
